import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import {
  getReconciliationRunRequest,
  type ReconciliationRunRequest,
  runReconciliationNow,
} from 'src/pages/admin/services/vaultService';

import { RUN_POLL_INTERVAL_MS, RUN_POLL_LIMIT_MS } from './constants';
import { newRequestId, reconciliationErrorMessages, runOutcome } from './helpers';

export interface RunAlert {
  severity: 'error';
  messages: string[];
}

/**
 * XBIT-4864: "Run reconciliation now". The click mints one `requestId` that lives until the dialog closes: a POST that
 * failed (a timeout may still have queued the run) is retried with the SAME id, so the engine returns the existing request
 * and never queues a second run. After the POST the run request is read every RUN_POLL_INTERVAL_MS (QUEUED → RUNNING →
 * DEFERRED while the daily cut-off holds the vault → DONE with a verdict, or FAILED with an errorCode) until it is
 * terminal, the dialog closes, or RUN_POLL_LIMIT_MS passes. A run changes no freeze itself; a RECONCILED run after a
 * two-person resolution does, in the engine.
 */
export const useRunReconciliation = (vaultId: string | undefined, onSettled?: () => void) => {
  const translate = useTranslate();
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const [isOpen, setOpen] = useState(false);
  const [run, setRun] = useState<ReconciliationRunRequest | null>(null);
  const [alert, setAlert] = useState<RunAlert | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // False once polling gave up (read failed, or RUN_POLL_LIMIT_MS passed) on a run that is still moving: `resume` reads again.
  const [polling, setPolling] = useState(false);
  const requestId = useRef<string | null>(null);
  const inFlight = useRef(false);
  // The dialog's generation: a poll that outlives its dialog sees a newer number and stops.
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>();

  const stop = useCallback(() => {
    generation.current += 1;
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const poll = useCallback((id: string, vault: string, mine: number, startedAt: number) => {
    // One loop at a time: a resume replaces the pending tick rather than adding a second loop.
    if (timer.current) clearTimeout(timer.current);
    setPolling(true);
    const tick = async () => {
      if (mine !== generation.current) return;
      try {
        const next = await getReconciliationRunRequest(vault, id);
        if (mine !== generation.current) return;
        setRun(next);
        if (runOutcome(next).terminal) {
          setPolling(false);
          onSettledRef.current?.();
          return;
        }
      } catch (error) {
        if (mine !== generation.current) return;
        setAlert({
          severity: 'error',
          messages: reconciliationErrorMessages(error, translateRef.current),
        });
        setPolling(false);
        return;
      }
      if (Date.now() - startedAt >= RUN_POLL_LIMIT_MS) {
        setPolling(false);
        return;
      }
      timer.current = setTimeout(tick, RUN_POLL_INTERVAL_MS);
    };
    timer.current = setTimeout(tick, RUN_POLL_INTERVAL_MS);
  }, []);

  const open = useCallback(() => {
    stop();
    requestId.current = newRequestId();
    inFlight.current = false;
    setRun(null);
    setAlert(null);
    setSubmitting(false);
    setPolling(false);
    setOpen(true);
  }, [stop]);

  const close = useCallback(() => {
    stop();
    setOpen(false);
  }, [stop]);

  const submit = useCallback(async () => {
    const id = requestId.current;
    if (inFlight.current || !vaultId || !id) return;
    inFlight.current = true;
    setSubmitting(true);
    setAlert(null);
    const mine = generation.current;
    try {
      const queued = await runReconciliationNow(vaultId, id);
      if (mine !== generation.current) return;
      setRun(queued);
      if (runOutcome(queued).terminal) onSettledRef.current?.();
      else poll(id, vaultId, mine, Date.now());
    } catch (error) {
      if (mine !== generation.current) return;
      setAlert({
        severity: 'error',
        messages: reconciliationErrorMessages(error, translateRef.current),
      });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }, [poll, vaultId]);

  /** Read the run request again after polling gave up; the same requestId, so nothing is queued twice. */
  const resume = useCallback(() => {
    const id = requestId.current;
    if (!vaultId || !id || !run || polling) return;
    setAlert(null);
    poll(id, vaultId, generation.current, Date.now());
  }, [poll, polling, run, vaultId]);

  useEffect(() => stop, [stop]);

  return { isOpen, run, alert, submitting, polling, open, close, submit, resume };
};

export type RunReconciliationForm = ReturnType<typeof useRunReconciliation>;
